import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { getPermissions, type PermisosUsuarioDTO } from './permisosService';
import { useAuthStore } from './auth-store';

/**
 * @constant VIGENCIA_PERMISOS_MS
 * @description Tiempo durante el que una carga exitosa de permisos no se repite al cambiar de
 * pantalla ni al volver a la pestaña. Un cambio de rol o de actividades hecho por otra persona
 * se ve, como mucho, en el primer cambio de pantalla o regreso a la pestaña pasado este tiempo.
 */
export const VIGENCIA_PERMISOS_MS = 60_000;

/**
 * @constant LIMITE_PEDIDO_PERMISOS_MS
 * @description Tiempo máximo de un pedido de permisos. Pasado este tiempo se cancela y cuenta como
 * fallo: un pedido colgado no puede dejar trabada la carga en vuelo (y con ella todos los refrescos).
 */
export const LIMITE_PEDIDO_PERMISOS_MS = 15_000;

/**
 * @interface OpcionesRefrescoPermisos
 * @description Opciones de `refrescarPermisos`.
 */
export interface OpcionesRefrescoPermisos {
  /**
   * @property {boolean} [forzar] - Ignora la vigencia de la última carga. Si hay una carga en
   * vuelo, la repite una vez cuando termina (pudo salir antes del cambio que se acaba de guardar).
   * Se usa tras guardar un cambio de rol, de actividades o de permisos.
   */
  forzar?: boolean;
}

/**
 * `inicial`: la del inicio de sesión (`fetchPermissions`); marca `isLoading` y, si falla, vacía las
 * listas. `segundo-plano`: el refresco con la sesión ya en uso; no marca `isLoading` y, si falla,
 * conserva los permisos que había.
 */
type ModoCarga = 'inicial' | 'segundo-plano';

/** Carga en vuelo: nunca salen dos pedidos de permisos a la vez. */
let cargaEnCurso: Promise<void> | null = null;
/** Repetición pedida por un refresco forzado que llegó con otra carga en vuelo. */
let repeticionEncolada: Promise<void> | null = null;
/**
 * Momento (ms) de la última carga exitosa en esta página. `null` si todavía no hubo ninguna o si el
 * último refresco falló o trajo una lista vacía ignorada: el siguiente disparo reintenta sin esperar.
 */
let ultimaCargaExitosa: number | null = null;
/** Generación de la sesión: cambia al limpiar los permisos (logout) y descarta respuestas anteriores. */
let generacionSesion = 0;

/**
 * Secciones (`ClaveSeccion`) y actividades (`ClavesActividad`) únicas de la respuesta del SSO.
 */
const extraerClaves = (permisos: PermisosUsuarioDTO[]) => {
  const seccionesUnicas = new Set<string>();
  const actividadesUnicas = new Set<string>();
  permisos.forEach((permiso) => {
    if (permiso.ClaveSeccion && permiso.ClaveSeccion.trim()) {
      seccionesUnicas.add(permiso.ClaveSeccion.trim());
    }
    if (permiso.ClavesActividad && Array.isArray(permiso.ClavesActividad)) {
      permiso.ClavesActividad.forEach((actividad) => {
        if (actividad && actividad.trim()) {
          actividadesUnicas.add(actividad.trim());
        }
      });
    }
  });
  return {
    sectionPermissions: Array.from(seccionesUnicas),
    actionPermissions: Array.from(actividadesUnicas),
  };
};

/**
 * Conserva la lista actual si trae las mismas claves en el mismo orden: `useRolePermissions` memoriza
 * con la referencia de las listas, y un refresco sin cambios no debe recalcular los permisos de la app.
 */
const conservarSiIgual = (actual: string[], nueva: string[]): string[] =>
  actual.length === nueva.length && actual.every((clave, i) => clave === nueva[i]) ? actual : nueva;

/**
 * @interface PermissionsState
 * @description Define el estado de permisos de secciones y actividades del usuario
 * Los permisos vienen como listas de strings desde el endpoint
 */
export interface PermissionsState {
  /**
   * @property {string[]} sectionPermissions - Lista de secciones permitidas (viene del endpoint)
   * Ejemplo: ["GESTIONAR_TIPO_DOCUMENTO", "GESTIONAR_TIPO_PROVEEDOR", ...]
   */
  sectionPermissions: string[];

  /**
   * @property {string[]} actionPermissions - Lista de actividades/acciones permitidas (viene del endpoint)
   * Ejemplo: ["CREAR_TIPO_DOCUMENTO", "EDITAR_TIPO_DOCUMENTO", "ELIMINAR_TIPO_DOCUMENTO", ...]
   */
  actionPermissions: string[];

  /**
   * @property {boolean} isLoading - Indica si se están cargando los permisos
   */
  isLoading: boolean;

  /**
   * @property {string | null} error - Mensaje de error si falla la carga
   */
  error: string | null;

  /**
   * @function setPermissions
   * @description Establece las listas de permisos de secciones y actividades
   */
  setPermissions: (sections: string[], actions: string[]) => void;

  /**
   * @function hasSectionPermission
   * @description Verifica si una sección está en la lista de permisos
   */
  hasSectionPermission: (section: string) => boolean;

  /**
   * @function hasActionPermission
   * @description Verifica si una actividad está en la lista de permisos
   */
  hasActionPermission: (action: string) => boolean;

  /**
   * @function clearPermissions
   * @description Limpia todos los permisos (útil al hacer logout)
   */
  clearPermissions: () => void;

  /**
   * @function fetchPermissions
   * @description Carga de permisos del inicio de sesión: marca `isLoading` mientras dura y, si falla,
   * deja las listas vacías con `error`. Ignora la vigencia de la última carga y nunca corre en paralelo
   * con otra (si hay una en vuelo, se repite al terminar). Nunca rechaza.
   */
  fetchPermissions: () => Promise<void>;

  /**
   * @function refrescarPermisos
   * @description Pone al día los permisos del usuario con sesión sin bloquear la pantalla: no marca
   * `isLoading` y, si falla o no responde en `LIMITE_PEDIDO_PERMISOS_MS`, conserva los permisos que
   * había (con `error`). Con permisos cargados tampoco aplica una lista vacía. En esos casos el
   * siguiente disparo reintenta aunque no haya pasado la vigencia. Sin forzar no pide nada si la
   * última carga exitosa tiene menos de `VIGENCIA_PERMISOS_MS`. Sin token no sale ningún pedido.
   * Nunca rechaza.
   * @param {OpcionesRefrescoPermisos} [opciones] - `forzar` tras guardar un cambio de permisos.
   */
  refrescarPermisos: (opciones?: OpcionesRefrescoPermisos) => Promise<void>;
}

/**
 * @constant usePermissionsStore
 * @description Store para gestionar listas de permisos de secciones y actividades
 */
export const usePermissionsStore = create<PermissionsState>()(
  persist(
    (set, get) => ({
      sectionPermissions: [],
      actionPermissions: [],
      isLoading: false,
      error: null,

      setPermissions: (sections: string[], actions: string[]) => {
        set({
          sectionPermissions: sections,
          actionPermissions: actions,
        });
      },

      hasSectionPermission: (section: string): boolean => {
        const { sectionPermissions } = get();
        // Si la lista está vacía, retornar false (o true si quieres permitir por defecto)
        if (sectionPermissions.length === 0) {
          return false;
        }
        // Verificar si la sección está en la lista (case-insensitive)
        return sectionPermissions.some(
          (perm) => perm.toLowerCase().trim() === section.toLowerCase().trim()
        );
      },

      hasActionPermission: (action: string): boolean => {
        const { actionPermissions } = get();
        // Si la lista está vacía, retornar false
        if (actionPermissions.length === 0) {
          return false;
        }
        // Verificar si la actividad está en la lista (case-insensitive)
        return actionPermissions.some(
          (perm) => perm.toLowerCase().trim() === action.toLowerCase().trim()
        );
      },

      clearPermissions: () => {
        // Cierra la sesión de las cargas: lo que esté en vuelo se descarta y no se repite.
        generacionSesion += 1;
        cargaEnCurso = null;
        repeticionEncolada = null;
        ultimaCargaExitosa = null;
        set({
          sectionPermissions: [],
          actionPermissions: [],
          error: null,
          isLoading: false,
        });
      },

      fetchPermissions: () => solicitarCarga('inicial', true),

      refrescarPermisos: (opciones) => solicitarCarga('segundo-plano', opciones?.forzar === true),
    }),
    {
      name: 'permissions-storage',
      partialize: (state) => ({
        sectionPermissions: state.sectionPermissions,
        actionPermissions: state.actionPermissions,
      }),
    }
  )
);

/**
 * Un pedido al SSO. Nunca rechaza: el resultado (o el error) queda en el store según el modo.
 */
async function cargarPermisos(modo: ModoCarga): Promise<void> {
  const { idUser, token } = useAuthStore.getState();
  const generacionDeLaCarga = generacionSesion;

  if (!idUser || idUser <= 0 || (modo === 'segundo-plano' && !token)) {
    if (modo === 'inicial') {
      console.warn('No hay usuario autenticado para obtener permisos');
      usePermissionsStore.setState({ sectionPermissions: [], actionPermissions: [], isLoading: false });
    }
    return;
  }

  if (modo === 'inicial') usePermissionsStore.setState({ isLoading: true, error: null });
  try {
    const claves = extraerClaves(await pedirPermisosConLimite(idUser));
    // La sesión terminó mientras el pedido volaba: la respuesta ya no es de nadie.
    if (generacionDeLaCarga !== generacionSesion) return;
    const actual = usePermissionsStore.getState();
    if (modo === 'segundo-plano' && esVaciaConPermisosCargados(claves, actual)) {
      // El SSO responde `[]` con 200 cuando falla por dentro (`PermisosService.ObtenerPermisosxIDUsuario`
      // atrapa la excepción del repositorio): un refresco no puede distinguirlo de "le quitaron todos
      // los permisos". Se conservan los que había y, sin marcar la carga como exitosa, el siguiente
      // disparo reintenta. Quitar de verdad todos los permisos se ve en el siguiente login (que aplica
      // lo que llegue), y el back sigue autorizando cada pedido.
      ultimaCargaExitosa = null;
      return;
    }
    ultimaCargaExitosa = Date.now();
    usePermissionsStore.setState({
      sectionPermissions: conservarSiIgual(actual.sectionPermissions, claves.sectionPermissions),
      actionPermissions: conservarSiIgual(actual.actionPermissions, claves.actionPermissions),
      isLoading: false,
      error: null,
    });
  } catch (error) {
    if (generacionDeLaCarga !== generacionSesion) return;
    // Un fallo (o un pedido cortado por el límite) no frena el siguiente disparo, aunque no haya
    // pasado la vigencia de la última carga exitosa.
    ultimaCargaExitosa = null;
    console.error('Error fetching permissions:', error);
    usePermissionsStore.setState(
      modo === 'inicial'
        ? { error: 'Error al cargar permisos', isLoading: false, sectionPermissions: [], actionPermissions: [] }
        : { error: 'Error al cargar permisos' }
    );
  }
}

/** La respuesta no trae ninguna clave y el usuario tiene permisos cargados. */
function esVaciaConPermisosCargados(
  claves: { sectionPermissions: string[]; actionPermissions: string[] },
  actual: PermissionsState
): boolean {
  const vacia = claves.sectionPermissions.length === 0 && claves.actionPermissions.length === 0;
  const teniaPermisos = actual.sectionPermissions.length > 0 || actual.actionPermissions.length > 0;
  return vacia && teniaPermisos;
}

/**
 * Pide los permisos con un tope de `LIMITE_PEDIDO_PERMISOS_MS`: al vencer cancela el pedido HTTP y
 * rechaza, así la carga en vuelo se libera aunque el SSO no conteste. No cambia el `timeout` del
 * cliente `apiSSO`, que comparten todas las pantallas.
 */
async function pedirPermisosConLimite(idUser: number): Promise<PermisosUsuarioDTO[]> {
  const controlador = new AbortController();
  let corte: ReturnType<typeof setTimeout> | undefined;
  const limite = new Promise<never>((_, rechazar) => {
    corte = setTimeout(() => {
      controlador.abort();
      rechazar(new Error(`El pedido de permisos no respondió en ${LIMITE_PEDIDO_PERMISOS_MS} ms`));
    }, LIMITE_PEDIDO_PERMISOS_MS);
  });
  try {
    return await Promise.race([getPermissions(idUser, controlador.signal), limite]);
  } finally {
    clearTimeout(corte);
  }
}

/** Lanza una carga y la registra como la carga en vuelo hasta que termina. */
function iniciarCarga(modo: ModoCarga): Promise<void> {
  const carga: Promise<void> = cargarPermisos(modo).finally(() => {
    if (cargaEnCurso === carga) cargaEnCurso = null;
  });
  cargaEnCurso = carga;
  return carga;
}

/**
 * Punto único de entrada de las cargas: aplica la vigencia (salvo `forzar`) y garantiza que nunca
 * haya dos pedidos a la vez.
 */
function solicitarCarga(modo: ModoCarga, forzar: boolean): Promise<void> {
  if (!forzar && ultimaCargaExitosa !== null && Date.now() - ultimaCargaExitosa < VIGENCIA_PERMISOS_MS) {
    return Promise.resolve();
  }
  if (cargaEnCurso === null) return iniciarCarga(modo);
  if (!forzar) return cargaEnCurso;
  // La carga en vuelo pudo salir antes del cambio recién guardado: se repite una sola vez al terminar
  // (nunca en paralelo), y los forzados que lleguen mientras tanto comparten esa repetición.
  if (repeticionEncolada === null) {
    const generacionDelPedido = generacionSesion;
    const repeticion: Promise<void> = cargaEnCurso.then(() => {
      if (repeticionEncolada === repeticion) repeticionEncolada = null;
      if (generacionDelPedido !== generacionSesion) return undefined;
      return cargaEnCurso ?? iniciarCarga(modo);
    });
    repeticionEncolada = repeticion;
  }
  return repeticionEncolada;
}

